import { useEffect, useState } from "react";
import { X, DownloadSimple, Share, DeviceMobile } from "@phosphor-icons/react";
import { trackConversion } from "../lib/analytics";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator?.standalone === true;
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isMobile = () => window.matchMedia?.("(max-width: 768px)").matches || isIOS();
const cookieBannerOpen = () => { try { return !localStorage.getItem("freightpilot_cookie_consent_v1"); } catch { return false; } };

/** Floating one-tap "Add to home screen" sheet. Shows once per source, after cookie consent, once `when` is true. */
export default function HomeScreenPrompt({ source = "landing", delayMs = 6000, when = true }) {
  const key = `fp_home_screen_prompt_${source}`;
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [showHow, setShowHow] = useState(false);

  useEffect(() => {
    if (!when) return;
    try { if (isStandalone() || localStorage.getItem(key)) return; } catch { return; }
    let deferredEvt = null;
    let timer = null;
    const onBeforeInstall = (e) => { e.preventDefault(); deferredEvt = e; setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (cookieBannerOpen()) return;
        if (isMobile() || deferredEvt) { setVisible(true); trackConversion(`${source}_install_prompt_shown`); }
      }, delayMs);
    };
    arm();
    window.addEventListener("fp:cookie-consent", arm);
    const onInstalled = () => dismiss("installed");
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("fp:cookie-consent", arm);
      window.removeEventListener("appinstalled", onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [when]);

  const dismiss = (why = "dismissed") => {
    try { localStorage.setItem(key, `${why}:${new Date().toISOString()}`); } catch {}
    setVisible(false);
  };

  const install = async () => {
    trackConversion(`${source}_install_prompt_tap`);
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      dismiss(outcome === "accepted" ? "installed" : "declined");
      return;
    }
    setShowHow(true);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="home-screen-prompt"
      className="fixed inset-x-3 bottom-[76px] sm:bottom-6 sm:left-auto sm:right-6 sm:w-[380px] z-40 rounded-2xl border border-[#FF5A1F]/40 bg-[#111]/95 backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-4 animate-in slide-in-from-bottom-4 fade-in duration-500"
    >
      <div className="flex items-start gap-3">
        <img src="/pwa-icon-192.png" alt="" className="w-11 h-11 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white leading-tight">Add FreightPilot to your home screen</div>
          <div className="text-xs text-neutral-400 mt-1 leading-relaxed">
            {showHow ? (
              isIOS()
                ? <>Tap <Share size={12} className="inline align-middle text-white" /> at the bottom of Safari, then <b className="text-white">Add to Home Screen</b>. Done — it opens like an app.</>
                : <>Open your browser menu (⋮) and tap <b className="text-white">Add to Home screen</b> or <b className="text-white">Install app</b>.</>
            ) : (
              "One tap — opens full screen with its own icon. No app store, no download wait."
            )}
          </div>
          {!showHow && (
            <button
              type="button"
              onClick={install}
              data-testid="home-screen-prompt-install"
              className="mt-3 rounded-full px-4 py-2 text-xs font-bold bg-[#FF5A1F] text-black hover:bg-[#FF7038] transition-colors inline-flex items-center gap-1.5"
            >
              {deferred ? <><DownloadSimple size={13} weight="bold" /> Install now</> : <><DeviceMobile size={13} weight="bold" /> Show me how</>}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => dismiss()}
          data-testid="home-screen-prompt-dismiss"
          aria-label="Dismiss"
          className="p-1 -mr-1 -mt-1 text-neutral-500 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
